import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { ChevronLeft, ChevronRight, Star } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * TestimonialsSection — public component shown on the homepage + promo pages.
 *
 * - Hits /api/testimonials and only renders if there are 1+ active items.
 * - ≤2 testimonials → side-by-side grid (no carousel chrome).
 * - 3+ testimonials → horizontal scroll carousel with left/right arrows on
 *   desktop. Mobile uses native scroll-snap so users can swipe.
 */
const TestimonialsSection = ({
  title = "Loved by pet parents",
  subtitle = "Real frames in real homes.",
  containerClass = "max-w-6xl mx-auto px-6 lg:px-10 py-16",
}) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const scrollerRef = useRef(null);

  useEffect(() => {
    let cancel = false;
    axios
      .get(`${API}/testimonials`)
      .then((res) => {
        if (cancel) return;
        setItems(res.data?.items || []);
      })
      .catch(() => {
        if (!cancel) setItems([]);
      })
      .finally(() => {
        if (!cancel) setLoading(false);
      });
    return () => {
      cancel = true;
    };
  }, []);

  if (loading || items.length === 0) return null;

  const showCarousel = items.length > 2;
  const scrollBy = (dir) => {
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollBy({
      left: dir * Math.min(el.clientWidth * 0.9, 480),
      behavior: "smooth",
    });
  };

  return (
    <section
      className="border-t border-[#EAE5DF] bg-[#FAF6F0]"
      data-testid="testimonials-section"
    >
      <div className={containerClass}>
        <div className="flex items-end justify-between mb-8 flex-wrap gap-3">
          <div>
            <p className="label-uppercase mb-2 text-[#C8A27A]">Testimonials</p>
            <h2 className="serif text-3xl sm:text-4xl text-[#1A1A1A]">
              {title}
            </h2>
            {subtitle && (
              <p className="text-sm text-[#6B6B6B] mt-1">{subtitle}</p>
            )}
          </div>
          {showCarousel && (
            <div className="hidden sm:flex items-center gap-2">
              <button
                type="button"
                onClick={() => scrollBy(-1)}
                aria-label="Previous"
                data-testid="testimonials-prev"
                className="w-9 h-9 rounded-full border border-[#D9D3CA] flex items-center justify-center hover:border-[#C8A27A] transition-colors text-[#1A1A1A]"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={() => scrollBy(1)}
                aria-label="Next"
                data-testid="testimonials-next"
                className="w-9 h-9 rounded-full border border-[#D9D3CA] flex items-center justify-center hover:border-[#C8A27A] transition-colors text-[#1A1A1A]"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          )}
        </div>

        {showCarousel ? (
          <div
            ref={scrollerRef}
            className="-mx-6 lg:-mx-10 px-6 lg:px-10 overflow-x-auto scrollbar-hide snap-x snap-mandatory"
            data-testid="testimonials-carousel"
          >
            <div className="flex gap-5 pb-2">
              {items.map((t) => (
                <div
                  key={t.id}
                  className="flex-shrink-0 w-[300px] sm:w-[360px] snap-start"
                >
                  <TestimonialCard t={t} />
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 gap-5" data-testid="testimonials-grid">
            {items.map((t) => (
              <TestimonialCard key={t.id} t={t} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

const TestimonialCard = ({ t }) => {
  const rating = Math.max(1, Math.min(5, t.rating || 5));
  const meta = [t.customer_name, t.customer_location, t.pet_name && `& ${t.pet_name}`]
    .filter(Boolean)
    .join(" · ");
  return (
    <article
      className="bg-card border border-[#EAE5DF] overflow-hidden flex flex-col h-full"
      data-testid={`testimonial-${t.id}`}
    >
      {t.image_url && (
        <div className="aspect-[4/3] bg-[#F3EEE6] overflow-hidden">
          <img
            src={t.image_url}
            alt={t.customer_name || "Customer testimonial"}
            loading="lazy"
            className="w-full h-full object-cover"
          />
        </div>
      )}
      <div className="p-5 flex flex-col flex-1">
        <div
          className="flex items-center gap-0.5 mb-3"
          aria-label={`${rating} out of 5 stars`}
        >
          {[1, 2, 3, 4, 5].map((i) => (
            <Star
              key={i}
              className={`h-4 w-4 ${
                i <= rating
                  ? "fill-[#C8A27A] text-[#C8A27A]"
                  : "text-[#D9D3CA]"
              }`}
            />
          ))}
        </div>
        <p className="text-sm text-[#1A1A1A] leading-relaxed mb-4 whitespace-pre-line">
          “{t.text}”
        </p>
        {meta && (
          <p className="text-xs label-uppercase text-[#6B6B6B] mt-auto">
            — {meta}
          </p>
        )}
      </div>
    </article>
  );
};

export default TestimonialsSection;
